import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from './SocketContext';
import { api } from '../api/apiClient';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const socket = useSocket(); // context value IS the socket instance (or null until connected)
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const currentRequestId = useRef(null);
  const streamBuffer = useRef('');

  useEffect(() => {
    if (!socket) return;

    const onQueued = ({ requestId, position }) => {
      if (requestId === currentRequestId.current) setQueuePosition(position);
    };
    const onToken = ({ requestId, token }) => {
      if (requestId !== currentRequestId.current) return;
      setQueuePosition(null);
      streamBuffer.current += token;
      const partial = streamBuffer.current;
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: partial, streaming: true };
        return copy;
      });
    };
    const onDone = ({ requestId, fullMessage }) => {
      if (requestId !== currentRequestId.current) return;
      currentRequestId.current = null;
      setIsStreaming(false);
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: fullMessage, streaming: false };
        return copy;
      });
    };
    const onError = ({ requestId, message }) => {
      if (requestId !== currentRequestId.current) return;
      currentRequestId.current = null;
      setIsStreaming(false);
      setQueuePosition(null);
      setMessages(prev => {
        const copy = [...prev];
        // Replace the empty streaming placeholder if the error came before any token
        if (copy.length > 0 && copy[copy.length - 1].streaming) copy.pop();
        return [...copy, { role: 'assistant', content: message, error: true }];
      });
    };

    socket.on('chatbot:queued', onQueued);
    socket.on('chatbot:token', onToken);
    socket.on('chatbot:done', onDone);
    socket.on('chatbot:error', onError);
    return () => {
      socket.off('chatbot:queued', onQueued);
      socket.off('chatbot:token', onToken);
      socket.off('chatbot:done', onDone);
      socket.off('chatbot:error', onError);
    };
  }, [socket]);

  const sendMessage = useCallback((text, ctx) => {
    if (!socket || !text?.trim() || isStreaming) return;
    const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    currentRequestId.current = requestId;
    streamBuffer.current = '';
    setIsStreaming(true);
    setMessages(prev => [...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true }
    ]);
    socket.emit('chatbot:message', { requestId, message: text, ...ctx });
  }, [socket, isStreaming]);

  const loadHistory = useCallback(async (analysisId) => {
    try {
      const res = await api.get('/chatbot/history', analysisId ? { analysisId } : {});
      setMessages((res?.data || []).map(m => ({ role: m.role, content: m.content })));
    } catch {
      // History is best-effort — don't block the widget on it
    }
  }, []);

  const clearHistory = useCallback(async (analysisId) => {
    try {
      await api.delete('/chatbot/history', analysisId ? { analysisId } : {});
      setMessages([]);
    } catch {
      // best-effort
    }
  }, []);

  return (
    <ChatContext.Provider value={{ messages, isStreaming, queuePosition, sendMessage, loadHistory, clearHistory }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
